import React from "react";
import {
  Command,
  CommandInput,
  CommandList,
  CommandItem,
  CommandGroup,
  CommandEmpty,
} from "@src/ui/command";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "@src/ui/hover-card";
import { Button } from "@src/ui/button";
import Text from "@src/ui/text";
import { ChatThumbnail } from "../ChatAttachment/ChatThumbnail";
import { ChatAttachmentLibraryItemFragment } from "../ChatAttachment/index.generated";
import { LibraryItemType } from "@src/types.generated";
import { ChevronRightIcon, UploadIcon } from "lucide-react";

export interface SelectableItem {
  libraryItem: ChatAttachmentLibraryItemFragment;
}

export type FilterType = "Course" | "Resource" | null;

export interface ChatAttachMenuContentProps {
  selectableItems: SelectableItem[];
  selectedFilter: FilterType;
  setSelectedFilter: (filter: FilterType) => void;
  onSelectItem: (item: SelectableItem) => void;
  onUploadClick?: () => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  isLoading?: boolean;
}

export const LibraryItemHoverCard: React.FC<{
  libraryItem?: ChatAttachmentLibraryItemFragment | null;
  children: React.ReactNode;
  hoverCardContentProps?: Partial<
    React.ComponentProps<typeof HoverCardContent>
  >;
}> = ({ libraryItem, children, hoverCardContentProps }) => {
  return (
    <HoverCard openDelay={300} closeDelay={100}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent side="left" className="w-80" {...hoverCardContentProps}>
        <div className="flex flex-col gap-3">
          <div className="flex items-start gap-3">
            <ChatThumbnail
              libraryItemFragment={libraryItem}
              size={48}
              className="flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <Text type="P2" fontWeight="SemiBold" className="line-clamp-2">
                {libraryItem?.name.en}
              </Text>
              <Text type="P3" className="text-muted-foreground mt-1">
                {libraryItem?.type === LibraryItemType.Course
                  ? "Course"
                  : "Resource"}
              </Text>
            </div>
          </div>

          {/* Description would go here if available in the fragment */}

          <Button
            variant="outline"
            size="sm"
            className="w-fit"
            onClick={(e) => {
              e.stopPropagation();
              // TODO: Open library item detail page in new tab
              // This would need the library item URL/route
            }}
          >
            View Details
          </Button>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

const LibraryItemRow: React.FC<{
  item: SelectableItem;
  onSelect: (item: SelectableItem) => void;
}> = ({ item, onSelect }) => {
  const { libraryItem } = item;
  const contentType =
    libraryItem.type === LibraryItemType.Course ? "Course" : "Resource";
  console.log("libraryItem", libraryItem);
  return (
    <LibraryItemHoverCard libraryItem={item.libraryItem}>
      <CommandItem
        value={libraryItem.id}
        onSelect={() => onSelect(item)}
        className="flex items-center gap-1.5 px-1.5 py-1.5 cursor-pointer"
      >
        <ChatThumbnail
          libraryItemFragment={libraryItem}
          size={20}
          className="flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <Text type="P2" className="line-clamp-1">
            {libraryItem.name.en}
          </Text>
        </div>
        <Text type="P3" className="text-muted-foreground flex-shrink-0">
          {contentType}
        </Text>
      </CommandItem>
    </LibraryItemHoverCard>
  );
};

export const ChatAttachMenuContent: React.FC<ChatAttachMenuContentProps> = ({
  selectableItems,
  selectedFilter,
  setSelectedFilter,
  onSelectItem,
  onUploadClick,
  searchValue,
  onSearchChange,
  isLoading = false,
}) => {
  const showMainMenu = !selectedFilter && !searchValue;

  return (
    <Command className="w-[400px]" shouldFilter={false}>
      <CommandInput
        placeholder="Search library items..."
        value={searchValue}
        onValueChange={onSearchChange}
        className="border-0"
      />

      <CommandList className="max-h-[400px]overflow-y-auto">
        {showMainMenu && (
          <CommandGroup>
            <CommandItem
              onSelect={() => setSelectedFilter("Course")}
              className="flex items-center gap-2 px-3 py-2 cursor-pointer"
            >
              <Text type="P2">Courses</Text>
              <ChevronRightIcon
                size={16}
                className="ml-auto text-muted-foreground"
              />
            </CommandItem>

            <CommandItem
              onSelect={() => setSelectedFilter("Resource")}
              className="flex items-center gap-2 px-3 py-2 cursor-pointer"
            >
              <Text type="P2">Resources</Text>
              <ChevronRightIcon
                size={16}
                className="ml-auto text-muted-foreground"
              />
            </CommandItem>

            {onUploadClick && (
              <>
                <CommandItem
                  onSelect={onUploadClick}
                  className="flex items-center gap-2 px-3 py-2 cursor-pointer"
                >
                  <UploadIcon size={16} />
                  <Text type="P2">Upload files</Text>
                </CommandItem>
              </>
            )}
          </CommandGroup>
        )}

        {!showMainMenu && (
          <CommandGroup>
            {selectableItems.length === 0 ? (
              <CommandEmpty>No items found.</CommandEmpty>
            ) : (
              selectableItems.map((item) => {
                console.log("item", item);
                return (
                  <LibraryItemRow
                    key={item.libraryItem.id}
                    item={item}
                    onSelect={onSelectItem}
                  />
                );
              })
            )}
          </CommandGroup>
        )}
      </CommandList>
    </Command>
  );
};
